/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Box,
  Typography,
  Grid,
  Switch,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Divider,
  FormControlLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEventData } from "../EventDataContext";

const SectionTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
    <Box sx={{ width: '4px', height: '24px', backgroundColor: '#8A5918', mr: 1, borderRadius: '2px' }} />
    <Typography
      variant="h6"
      sx={{
        color: "#1A1A1A",
        fontWeight: 700,
        fontSize: "18px",
        textAlign: "left",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Label = ({ children, required }) => (
  <Typography
    sx={{
      color: "#4A4A4A",
      fontWeight: 600,
      fontSize: "14px",
      mb: 1,
      textAlign: "left",
    }}
  >
    {children} {required && <span style={{ color: "#E94C4C" }}>*</span>}
  </Typography>
);

export default function AlbumSettingsPage() {
  const { eventData, updateNestedEventData, validationErrors } = useEventData();
  const { album } = eventData;

  const handleLayoutChange = (field, value) => {
    updateNestedEventData('album', 'layout', { [field]: { value } });
  };

  const handleLikeVoteChange = (field, value) => {
    updateNestedEventData('album', 'likeVote', { [field]: { value } });
  };

  const handleWishTextDisplayChange = (field, value) => {
    updateNestedEventData('album', 'wishTextDisplay', { [field]: { value } });
  };

  return (
    <Box>
      <SectionTitle>Layout Settings</SectionTitle>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Label>Sorting Types</Label>
          <FormControl fullWidth error={!!validationErrors?.album?.layout?.sortingType}>
            <Select
              value={album.layout.sortingType.value}
              onChange={(e) => handleLayoutChange('sortingType', e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.sortingType ? "#d32f2f" : "#E0E0E0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.sortingType ? "#d32f2f" : "#BDBDBD",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.sortingType ? "#d32f2f" : "#8A5918",
                },
                "& .MuiSelect-select": {
                  padding: "12px 16px",
                },
              }}
              IconComponent={ExpandMoreIcon}
            >
              <MenuItem value="time">Sort by Time</MenuItem>
              <MenuItem value="like">Sort by Likes</MenuItem>
            </Select>
            {validationErrors?.album?.layout?.sortingType && (
              <FormHelperText sx={{ color: "#d32f2f", ml: 1 }}>
                {validationErrors.album.layout.sortingType}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12}>
          <Label>Picture Frame Size</Label>
          <FormControl fullWidth error={!!validationErrors?.album?.layout?.pictureFrameSize}>
            <Select
              value={album.layout.pictureFrameSize.value}
              onChange={(e) => handleLayoutChange('pictureFrameSize', e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.pictureFrameSize ? "#d32f2f" : "#E0E0E0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.pictureFrameSize ? "#d32f2f" : "#BDBDBD",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.pictureFrameSize ? "#d32f2f" : "#8A5918",
                },
                "& .MuiSelect-select": {
                  padding: "12px 16px",
                },
              }}
              IconComponent={ExpandMoreIcon}
            >
              <MenuItem value="S">Small (S)</MenuItem>
              <MenuItem value="M">Medium (M)</MenuItem>
              <MenuItem value="L">Large (L)</MenuItem>
            </Select>
            {validationErrors?.album?.layout?.pictureFrameSize && (
              <FormHelperText sx={{ color: "#d32f2f", ml: 1 }}>
                {validationErrors.album.layout.pictureFrameSize}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12}>
          <Label>Number of Columns</Label>
          <FormControl fullWidth error={!!validationErrors?.album?.layout?.numberOfColumns}>
            <Select
              value={album.layout.numberOfColumns.value}
              onChange={(e) => handleLayoutChange('numberOfColumns', e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.numberOfColumns ? "#d32f2f" : "#E0E0E0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.numberOfColumns ? "#d32f2f" : "#BDBDBD",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: validationErrors?.album?.layout?.numberOfColumns ? "#d32f2f" : "#8A5918",
                },
                "& .MuiSelect-select": {
                  padding: "12px 16px",
                },
              }}
              IconComponent={ExpandMoreIcon}
            >
              <MenuItem value="1">1 Column</MenuItem>
              <MenuItem value="2">2 Columns</MenuItem>
              <MenuItem value="3">3 Columns</MenuItem>
            </Select>
            {validationErrors?.album?.layout?.numberOfColumns && (
              <FormHelperText sx={{ color: "#d32f2f", ml: 1 }}>
                {validationErrors.album.layout.numberOfColumns}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <SectionTitle>Like / Vote Settings</SectionTitle>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
            <Label>Enable Like/Vote</Label>
            <Switch
              checked={album.likeVote.enabled.value}
              onChange={(e) => handleLikeVoteChange('enabled', e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
            <Label>Show Number of Likes</Label>
            <Switch
              checked={album.likeVote.showNumberOfLikes.value}
              onChange={(e) => handleLikeVoteChange('showNumberOfLikes', e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ my: 4 }}>
        <Divider />
      </Box>

      <SectionTitle>Wish Text Display Settings</SectionTitle>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
            <Label>Show Wisher Name</Label>
            <Switch
              checked={album.wishTextDisplay.showWisherName.value}
              onChange={(e) => handleWishTextDisplayChange('showWisherName', e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={12}>
          <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
            <Label>Show View Wishes</Label>
            <Switch
              checked={album.wishTextDisplay.showViewWishes.value}
              onChange={(e) => handleWishTextDisplayChange('showViewWishes', e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#8A5918",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#8A5918",
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
